import { useMemo, useState, type FormEvent, type KeyboardEvent, type ReactNode } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Check, Loader2, PencilLine, X } from "lucide-react";
import { toast } from "sonner";

import { DocumentSubRow } from "@/components/projects/document-sub-row";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SHEET_PAGE_TYPES,
  type RevisionSheet,
  type SheetPageType,
  useRevisionSheets,
} from "@/features/sheet-index/use-revision-sheets";
import { toTitleCase } from "@/lib/formatting";
import { notifyError } from "@/lib/notify-error";
import type { GlobalProcessingState } from "@/lib/processing-states";
import { REVEAL_ON_ROW_HOVER } from "@/lib/utils";

const ALL = "__all__";
/** Rows rendered before "Show all"; long plan sets run to hundreds of pages. */
const INITIAL_VISIBLE_SHEETS = 25;

interface RevisionSheetIndexProps {
  revisionId: Id<"projectDocumentRevisions">;
  pageIndexingStatus?: GlobalProcessingState;
  canEdit: boolean;
  onOpenPage: (pageNumber: number) => void;
}

function distinctSorted<T extends string>(values: (T | undefined)[]) {
  return Array.from(new Set(values.filter((value): value is T => Boolean(value)))).sort();
}

function SheetMessageRow({ children }: { children: ReactNode }) {
  return <DocumentSubRow name={<span className="text-sm text-muted-foreground">{children}</span>} />;
}

/** Page number column; "· Edited" marks a user correction. */
function PageCell({ sheet }: { sheet: RevisionSheet }) {
  return (
    <span className="text-xs text-muted-foreground tabular-nums">
      Page {sheet.pageNumber}
      {sheet.hasUserOverride ? " · Edited" : null}
    </span>
  );
}

function SheetFilter({
  label,
  allLabel,
  value,
  options,
  onChange,
}: {
  label: string;
  allLabel: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger size="sm" className="w-auto min-w-36" aria-label={label}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{allLabel}</SelectItem>
        {options.map((option) => (
          <SelectItem key={option} value={option}>
            {toTitleCase(option)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function SheetEditRow({ sheet, onDone }: { sheet: RevisionSheet; onDone: () => void }) {
  const updatePageOverrides = useMutation(api.projectDocumentPages.updatePageOverrides);
  const [sheetNumber, setSheetNumber] = useState(sheet.sheetNumber ?? "");
  const [sheetTitle, setSheetTitle] = useState(sheet.sheetTitle ?? "");
  const [pageType, setPageType] = useState<SheetPageType>(sheet.pageType);
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    try {
      await updatePageOverrides({ pageId: sheet._id, sheetNumber, sheetTitle, pageType });
      toast.success("Sheet updated");
      onDone();
    } catch (error) {
      notifyError(error, "Failed to update sheet");
    } finally {
      setIsSaving(false);
    }
  };
  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && !isSaving) onDone();
  };

  const formId = `sheet-edit-${sheet._id}`;
  return (
    <DocumentSubRow
      className="bg-muted/30 hover:bg-muted/30"
      onKeyDown={handleKeyDown}
      name={
        <div className="flex w-full max-w-md items-center gap-2">
          <form id={formId} onSubmit={(event) => void handleSubmit(event)} />
          <Input
            form={formId}
            aria-label={`Sheet number for page ${sheet.pageNumber}`}
            placeholder="Sheet"
            value={sheetNumber}
            onChange={(event) => setSheetNumber(event.currentTarget.value)}
            className="h-8 w-24 shrink-0"
            autoFocus
          />
          <Input
            form={formId}
            aria-label={`Sheet title for page ${sheet.pageNumber}`}
            placeholder="Title"
            value={sheetTitle}
            onChange={(event) => setSheetTitle(event.currentTarget.value)}
            className="h-8 min-w-0 flex-1"
          />
          <IconButton
            type="submit"
            form={formId}
            label="Save sheet"
            icon={isSaving ? Loader2 : Check}
            iconClassName={isSaving ? "animate-spin" : undefined}
            disabled={isSaving}
          />
          <IconButton label="Cancel editing" icon={X} onClick={onDone} disabled={isSaving} />
        </div>
      }
      revision={<PageCell sheet={sheet} />}
      type={
        <Select value={pageType} onValueChange={(value) => setPageType(value as SheetPageType)}>
          <SelectTrigger size="sm" className="w-full" aria-label={`Page type for page ${sheet.pageNumber}`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SHEET_PAGE_TYPES.map((type) => (
              <SelectItem key={type} value={type}>
                {toTitleCase(type)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      }
    />
  );
}

function SheetLabel({ sheet }: { sheet: RevisionSheet }) {
  if (!sheet.sheetNumber) {
    return <span className="truncate text-muted-foreground">{sheet.sheetTitle || `Page ${sheet.pageNumber}`}</span>;
  }
  return (
    <>
      <span className="shrink-0 font-medium text-foreground">{sheet.sheetNumber}</span>
      {sheet.sheetTitle ? <span className="truncate text-muted-foreground">{sheet.sheetTitle}</span> : null}
    </>
  );
}

/**
 * The pages of a revision as indented sub-rows of its document row: sheet
 * number and title read from title blocks, page number and page type. Rows
 * open the viewer at that page; editors correct a sheet inline.
 */
export function RevisionSheetIndex({
  revisionId,
  pageIndexingStatus,
  canEdit,
  onOpenPage,
}: RevisionSheetIndexProps) {
  const { data: sheets, isPending } = useRevisionSheets(revisionId);
  const [disciplineFilter, setDisciplineFilter] = useState(ALL);
  const [pageTypeFilter, setPageTypeFilter] = useState(ALL);
  const [editingPageId, setEditingPageId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);

  const disciplines = useMemo(
    () => distinctSorted((sheets ?? []).map((sheet) => sheet.discipline)),
    [sheets]
  );
  const pageTypes = useMemo(
    () => distinctSorted((sheets ?? []).map((sheet) => sheet.pageType)),
    [sheets]
  );
  // A single discipline or page type has nothing to filter by.
  const showDisciplineFilter = disciplines.length > 1;
  const showPageTypeFilter = pageTypes.length > 1;
  const matchingSheets = (sheets ?? []).filter(
    (sheet) =>
      (!showDisciplineFilter || disciplineFilter === ALL || sheet.discipline === disciplineFilter) &&
      (!showPageTypeFilter || pageTypeFilter === ALL || sheet.pageType === pageTypeFilter)
  );
  const isTruncated = !showAll && matchingSheets.length > INITIAL_VISIBLE_SHEETS;
  const visibleSheets = isTruncated ? matchingSheets.slice(0, INITIAL_VISIBLE_SHEETS) : matchingSheets;

  if (isPending) {
    return (
      <SheetMessageRow>
        <span className="flex items-center gap-2">
          <Loader2 className="size-4 animate-spin" aria-hidden="true" />
          Loading sheets
        </span>
      </SheetMessageRow>
    );
  }

  if (!sheets || sheets.length === 0) {
    return (
      <SheetMessageRow>
        {pageIndexingStatus === "failed"
          ? "Sheets could not be read from this revision."
          : pageIndexingStatus === "completed"
            ? "No sheets were found in this revision."
            : "Reading sheet title blocks. Sheets appear here as pages are indexed."}
      </SheetMessageRow>
    );
  }

  return (
    <>
      {showDisciplineFilter || showPageTypeFilter ? (
        <DocumentSubRow
          name={
            <div className="flex flex-wrap items-center gap-2">
              {showDisciplineFilter ? (
                <SheetFilter
                  label="Filter sheets by discipline"
                  allLabel="All Disciplines"
                  value={disciplineFilter}
                  options={disciplines}
                  onChange={setDisciplineFilter}
                />
              ) : null}
              {showPageTypeFilter ? (
                <SheetFilter
                  label="Filter sheets by page type"
                  allLabel="All Page Types"
                  value={pageTypeFilter}
                  options={pageTypes}
                  onChange={setPageTypeFilter}
                />
              ) : null}
              {matchingSheets.length < sheets.length ? (
                <span className="text-xs text-muted-foreground tabular-nums">
                  {matchingSheets.length} of {sheets.length} pages
                </span>
              ) : null}
            </div>
          }
        />
      ) : null}
      {matchingSheets.length === 0 ? <SheetMessageRow>No sheets match these filters.</SheetMessageRow> : null}
      {visibleSheets.map((sheet) =>
        editingPageId === sheet._id ? (
          <SheetEditRow key={sheet._id} sheet={sheet} onDone={() => setEditingPageId(null)} />
        ) : (
          <DocumentSubRow
            key={sheet._id}
            className="group cursor-pointer hover:bg-muted/40"
            onClick={() => onOpenPage(sheet.pageNumber)}
            name={
              <div className="flex min-w-0 max-w-md items-center gap-2">
                {/* The click bubbles to the row; the button gives keyboard users the same action. */}
                <button
                  type="button"
                  className="flex min-w-0 items-baseline gap-2 text-left text-sm"
                  aria-label={`Open page ${sheet.pageNumber}`}
                >
                  <SheetLabel sheet={sheet} />
                </button>
                {canEdit ? (
                  <IconButton
                    label={`Edit page ${sheet.pageNumber}`}
                    icon={PencilLine}
                    size="xs"
                    className={REVEAL_ON_ROW_HOVER}
                    onClick={(event) => {
                      event.stopPropagation();
                      setEditingPageId(sheet._id);
                    }}
                  />
                ) : null}
              </div>
            }
            revision={<PageCell sheet={sheet} />}
            type={
              <Badge variant="outline" size="sm">
                {toTitleCase(sheet.pageType)}
              </Badge>
            }
          />
        )
      )}
      {isTruncated ? (
        <DocumentSubRow
          name={
            <Button type="button" variant="link" size="xs" className="px-0" onClick={() => setShowAll(true)}>
              Show All {matchingSheets.length} Pages
            </Button>
          }
        />
      ) : null}
    </>
  );
}
